import { useEffect, useState } from "react";
import { PassportDetails } from "./passport-details";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import {
  IdentityVerificationCaseV1CaseDetailsApplicationPassportDetailsInput,
  ApplicantPassportDetailsContainerIdentityVerificationCaseV1UpdateDocument,
  ApplicantPassportDetailsContainerIdentityVerificationCaseV1DeleteDocument,
  ApplicantPassportDetailsContainerIdentityVerificationCaseV1CurrentDocument,
  ApplicantPassportDetailsContainerApplicantUserDocument,
  ApplicantPassportDetailsContainerIdentityVerificationCaseV1CurrentQuery,
  IdentityVerificationCaseV1CaseDetailsApplicationPassportDetails,
  FileInfo,
  PassportDetailsContainerCurrentFilesQueryDocument,
} from "../../../../generated";
import { lookupCountryQuery } from "./passport-details-country-query";
import { App, Empty, Skeleton } from "antd";
import { useNavigate } from "react-router-dom";
import { Country } from "../../../../types/country";
import { ComponentQueryLoader } from "../../../shared/component-query-loader";
import { AzureBlobResponse } from "../../../shared/file-upload/azure-upload";
import { setItem } from "../../../shared/local-storage";

interface PassportDetailsContainerProps {
  applicantUserId: string;
}

export const PassportDetailsContainer: React.FC<PassportDetailsContainerProps> = (props) => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [isProfileSubmitted, setIsProfileSubmitted] = useState<boolean | null>(null);
  const [identityVerificationCaseV1Current, setIdentityVerificationCaseV1Current] =
    useState<ApplicantPassportDetailsContainerIdentityVerificationCaseV1CurrentQuery>();
  const {
    data: countryData,
    error: countryError,
    loading: countryLoading,
  } = useQuery(lookupCountryQuery);
  const {
    data: applicantUserData,
    error: applicantUserError,
    loading: applicantUserLoading,
  } = useQuery(ApplicantPassportDetailsContainerApplicantUserDocument, {
    variables: {
      applicantUserId: props.applicantUserId,
    },
  });
  const [getIdentityVerificationCaseV1Current, { loading: caseLoading, error: caseError }] = useLazyQuery(
    ApplicantPassportDetailsContainerIdentityVerificationCaseV1CurrentDocument
  );
  const [updatePassportDetails] = useMutation(ApplicantPassportDetailsContainerIdentityVerificationCaseV1UpdateDocument);
  const [deleteIdCase] = useMutation(ApplicantPassportDetailsContainerIdentityVerificationCaseV1DeleteDocument);

  const [getFiles] = useLazyQuery(PassportDetailsContainerCurrentFilesQueryDocument);

  const fetchFilesData = async () => {
    try {
      const { data } = await getFiles({
        variables: {
          applicantId: props.applicantUserId,
        },
      });
      return data?.identityVerificationCaseV1Current?.files as FileInfo[];
    } catch (error) {
      return null;
    }
  };

  // Update the profile submission status when applicant user data is fetched
  useEffect(() => {
    if (applicantUserData) {
      setIsProfileSubmitted(applicantUserData.applicantUser?.isProfileSubmitted as boolean);
    }
  }, [applicantUserData]);

  // Fetch identity verification case details if the profile is not submitted, or navigate to /cases if submitted
  useEffect(() => {
    if (isProfileSubmitted !== null || isProfileSubmitted !== undefined) {
      if (isProfileSubmitted === false) {
        const fetchIdentityVerificationCase = async () => {
          const result = await getIdentityVerificationCaseV1Current({
            variables: {
              applicantId: props.applicantUserId,
            },
            fetchPolicy: "network-only", // Ensure the query fetches new data
          });
          setItem('caseId', result?.data?.identityVerificationCaseV1Current?.id);
          setIdentityVerificationCaseV1Current(result.data);
        };

        fetchIdentityVerificationCase();
      } else if (isProfileSubmitted === true) {
        navigate(`/applicant/cases`);
      }
    }
  }, [isProfileSubmitted]);

  if (countryError || !countryData?.countries?.eventPayload?.countries) {
    return <Empty description={`There was an error loading the country data. Please try again! ${countryError}`} />;
  }

  if (applicantUserError) {
    return <Empty description={`There was an error loading the user data. Please try again! ${applicantUserError}`} />;
  }

  if (!identityVerificationCaseV1Current) {
    return <Skeleton active />;
  }

  const handleSave = async (values: IdentityVerificationCaseV1CaseDetailsApplicationPassportDetailsInput) => {
    try {
      const cleanedUpValues = Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value === null ? undefined : value]));
      const result = await updatePassportDetails({
        variables: {
          input: {
            id: identityVerificationCaseV1Current.identityVerificationCaseV1Current?.id,
            caseDetails: {
              application: {
                passportDetails: {
                  ...cleanedUpValues,
                },
              },
            },
          },
        },
      });

      if (result.data?.identityVerificationCaseV1Update.status.success) {
        message.success("Passport details saved successfully");
      } else {
        message.error("Failed to save passport details");
      }
    } catch (error) {
      message.error("Failed to save passport details");
    }
  };

  const handleSaveAndContinue = async (values: IdentityVerificationCaseV1CaseDetailsApplicationPassportDetailsInput) => {
    try {
      const cleanedUpValues = Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value === null ? undefined : value]));
      const result = await updatePassportDetails({
        variables: {
          input: {
            id: identityVerificationCaseV1Current.identityVerificationCaseV1Current?.id,
            caseDetails: {
              application: {
                passportDetails: {
                  ...cleanedUpValues,
                },
              },
            },
          },
        },
      });

      if (result.data?.identityVerificationCaseV1Update.status.success) {
        message.success("Passport details saved successfully");
        navigate(`/applicant/onboarding/id-verification-preview`);
      } else {
        message.error("Failed to save passport details");
      }
    } catch (error) {
      message.error("Failed to save passport details");
    }
  };

  const handleOnUploadSuccess = async (uploadedData: AzureBlobResponse, fieldName: string) => {
    const assetName = fieldName === "photo" ? "photoAssets" : "passportAssets";
    const { blobUrl, versionId } = uploadedData;
    const input = {
      id: identityVerificationCaseV1Current.identityVerificationCaseV1Current?.id,
      caseDetails: {
        application: {
          [assetName]: {
            [`${fieldName}`]: blobUrl,
            [`${fieldName}Version`]: versionId,
          },
        },
      },
    };
    updatePassportDetails({
      variables: {
        input,
      },
    }).then((response) => {
      if (response.data?.identityVerificationCaseV1Update.status.success) {
        message.success("File uploaded successfully");
      } else {
        message.error("Error saving Application data");
      }
    });
  };

  const handleWithdrawRequest = async () => {
    deleteIdCase({
      variables: {
        input: {
          caseId: identityVerificationCaseV1Current.identityVerificationCaseV1Current?.id,
        }
      },
    }).then((response) => {
      if (response.data?.identityVerificationCaseV1Delete.status.success) {
        message.success("Case withdrawn successfully");
        navigate('/applicant/onboarding')
      } else {
        message.error("Failed to withdraw case");
      }
    })
  };

  return (
    <ComponentQueryLoader
      error={applicantUserError || caseError}
      loading={countryLoading || applicantUserLoading || caseLoading}
      hasData={countryData?.countries?.eventPayload?.countries && identityVerificationCaseV1Current.identityVerificationCaseV1Current}
      hasDataComponent={
        <PassportDetails
          applicationPassportDetails={
            identityVerificationCaseV1Current.identityVerificationCaseV1Current?.caseDetails?.application
              ?.passportDetails as IdentityVerificationCaseV1CaseDetailsApplicationPassportDetails
          }
          countryData={countryData?.countries?.eventPayload?.countries as Country[]}
          onSave={handleSave}
          onSaveAndContinue={handleSaveAndContinue}
          onWithdrawRequest={handleWithdrawRequest}
          caseId={identityVerificationCaseV1Current.identityVerificationCaseV1Current?.id as string}
          fetchFilesData={fetchFilesData}
          onUploadSuccess={handleOnUploadSuccess}
        />
      }
    />
  );
};

